// Cloudflare D1 SQL State Synchronizer
// Acts as a non-blocking edge persistence pipeline that mirrors client-side localStorage state
// to Cloudflare's serverless D1 SQL database engine by default.
import { PracticeSession } from "../types";
import { encryptData, decryptData } from "./crypto";
import { api } from "./api";

function safeDispatchVedicEvent(name: string, detail?: any) {
  try {
    if (typeof window !== "undefined" && typeof (window as any).dispatchVedicEvent === "function") {
      (window as any).dispatchVedicEvent(name, detail);
    }
  } catch (err) {
    // silent
  }
}

export const SYNC_KEYS = [
  "calibrated_vocal_baseline",
  "vedic_vocal_warmup_streak",
  "vedic_vocal_warmup_completed_ids",
  "vedic_pancha_yajna",
  "vedic_hawan_active_step",
  "vedic_hawan_completed_steps",
  "vedic_unlocked_achievements",
  "vedic_tarpana_active_step",
  "vedic_tarpana_completed_steps",
  "vedic_manual_theme",
  "vedic_mantras_store",
  "vedic_settings",
];

// Designate sensitive client configuration states requiring transparent client-side encryption
export const ENCRYPTED_KEYS = [
  "calibrated_vocal_baseline",
  "vedic_pancha_yajna",
  "vedic_settings",
];

/**
 * Builds standard secure headers carrying the Authorization Bearer Token (Secure JWT / Sandbox Token)
 */
export function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("_vedic_auth_token") || "VedicDevSandboxToken_2026";
  return {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${token}`
  };
}

/**
 * Synchronizes a single key-value change to Cloudflare D1 SQL (excluding history which uses CRUD API)
 */
export async function syncKeyToD1(key: string, value: any) {
  if (!SYNC_KEYS.includes(key)) return;
  try {
    let stringValue = typeof value === "string" ? value : JSON.stringify(value);
    
    // Encrypt sensitive state keys prior to synchronization to guarantee Zero-Knowledge database privacy
    if (ENCRYPTED_KEYS.includes(key) && stringValue) {
      stringValue = await encryptData(stringValue);
    }

    await api.api.storage.$post(
      { json: { key, value: stringValue } },
      { headers: getAuthHeaders() }
    );
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] Background sync deferred. Operating in robust offline-first mode.", err);
  }
}

/**
 * Synchronizes all relevant local state items to Cloudflare D1 SQL in a single batch
 */
export async function syncAllToD1() {
  try {
    const syncData: Record<string, string> = {};
    for (const key of SYNC_KEYS) {
      const val = localStorage.getItem(key);
      if (val !== null) {
        let stringValue = val;
        // Zero-Knowledge batch encryption
        if (ENCRYPTED_KEYS.includes(key) && stringValue) {
          stringValue = await encryptData(stringValue);
        }
        syncData[key] = stringValue;
      }
    }

    if (Object.keys(syncData).length === 0) return;

    await api.api.storage.$post(
      { json: { syncData } },
      { headers: getAuthHeaders() }
    );
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] Batch sync failed. Reverting to persistent client cache.", err);
  }
}

/**
 * Helper to bypass the Storage Proxy's automatic database synchronization
 * during manual syncing operations to avoid redundant API triggers or loops.
 */
function runWithBypass(fn: () => void) {
  if (typeof window !== "undefined") {
    (window as any).__bypass_storage_proxy__ = true;
  }
  try {
    fn();
  } finally {
    if (typeof window !== "undefined") {
      (window as any).__bypass_storage_proxy__ = false;
    }
  }
}

/**
 * Pulls stored keys and practice history from Cloudflare D1 SQL and populates client-side localStorage.
 * Triggers events to refresh active states in UI.
 */
export async function pullFromD1(): Promise<boolean> {
  let updated = false;

  // 1. Pull key-value store values with auth
  try {
    const res = await api.api.storage.$get(
      {},
      { headers: getAuthHeaders() }
    );
    if (res.ok) {
      const data = await res.json() as any;
      if (!data.fallback && data.success && data.data) {
        for (const [key, val] of Object.entries(data.data)) {
          if (SYNC_KEYS.includes(key)) {
            let valToStore = val as string;
            // Decrypt on receipt from edge database
            if (ENCRYPTED_KEYS.includes(key) && valToStore) {
              valToStore = await decryptData(valToStore);
            }

            const currentLocal = localStorage.getItem(key);
            if (currentLocal !== valToStore) {
              // Direct set, temporary bypass proxy callback
              runWithBypass(() => {
                localStorage.setItem(key, valToStore);
              });
              updated = true;
            }
          }
        }
      }
    }
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] Unable to pull key-value store configurations.", err);
  }

  // 2. Pull practice history from relational database table and decrypt sensitive notes
  try {
    const historyRes = await api.api.history.$get(
      {},
      { headers: getAuthHeaders() }
    );
    if (historyRes.ok) {
      const historyData = await historyRes.json() as any;
      if (!historyData.fallback && historyData.success && Array.isArray(historyData.data)) {
        // Transparently decrypt personal journal notes field for each item
        const decryptedHistory: PracticeSession[] = [];
        for (const session of historyData.data) {
          const decryptedSession = { ...session };
          if (session.notes) {
            decryptedSession.notes = await decryptData(session.notes);
          }
          decryptedHistory.push(decryptedSession);
        }

        const currentLocalHistory = localStorage.getItem("vedic_history");
        const freshHistoryStr = JSON.stringify(decryptedHistory);
        if (currentLocalHistory !== freshHistoryStr) {
          runWithBypass(() => {
            localStorage.setItem("vedic_history", freshHistoryStr);
          });
          updated = true;
          safeDispatchVedicEvent("vedic-history-updated");
        }
      }
    }
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] Bypassed pulling practice history from SQL.", err);
  }

  if (updated) {
    safeDispatchVedicEvent("storage");
    safeDispatchVedicEvent("vedic-steps-updated");
  }
  return updated;
}

/**
 * --- Persistent Relational CRUD Operations for 'practice_history' in Cloudflare D1 ---
 */

export async function fetchPracticeHistoryFromD1(): Promise<PracticeSession[]> {
  try {
    const res = await api.api.history.$get(
      {},
      { headers: getAuthHeaders() }
    );
    if (res.ok) {
      const data = await res.json() as any;
      if (!data.fallback && data.success && Array.isArray(data.data)) {
        const decryptedHistory: PracticeSession[] = [];
        for (const session of data.data) {
          const decryptedSession = { ...session };
          if (session.notes) {
            decryptedSession.notes = await decryptData(session.notes);
          }
          decryptedHistory.push(decryptedSession);
        }

        runWithBypass(() => {
          localStorage.setItem("vedic_history", JSON.stringify(decryptedHistory));
        });
        safeDispatchVedicEvent("vedic-history-updated");
        safeDispatchVedicEvent("vedic-steps-updated");
        return decryptedHistory;
      }
    }
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] GET failed, fallback to offline local storage.", err);
  }
  const local = localStorage.getItem("vedic_history");
  return local ? JSON.parse(local) : [];
}

export async function savePracticeSessionToD1(session: PracticeSession): Promise<boolean> {
  // Update offline cache first
  const local = localStorage.getItem("vedic_history");
  let list: PracticeSession[] = local ? JSON.parse(local) : [];
  const idx = list.findIndex(s => s.id === session.id);
  if (idx > -1) {
    list[idx] = session;
  } else {
    list = [session, ...list];
  }
  
  // Set localStorage silently bypassing proxy loop
  if (typeof window !== "undefined") {
    runWithBypass(() => {
      localStorage.setItem("vedic_history", JSON.stringify(list));
    });
    safeDispatchVedicEvent("vedic-history-updated");
    safeDispatchVedicEvent("vedic-steps-updated");
  }

  try {
    // Zero-Knowledge Encryption on the sensitive journaling notes field prior to server submission
    const encryptedSession = { ...session };
    if (session.notes) {
      encryptedSession.notes = await encryptData(session.notes);
    }

    const res = await api.api.history.$post(
      { json: encryptedSession },
      { headers: getAuthHeaders() }
    );
    return res.ok;
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] POST failed, operations cached locally.", err);
    return false;
  }
}

export async function deletePracticeSessionFromD1(id: string): Promise<boolean> {
  const local = localStorage.getItem("vedic_history");
  if (local) {
    const list: PracticeSession[] = JSON.parse(local);
    const filtered = list.filter(s => s.id !== id);
    runWithBypass(() => {
      localStorage.setItem("vedic_history", JSON.stringify(filtered));
    });
    safeDispatchVedicEvent("vedic-history-updated");
    safeDispatchVedicEvent("vedic-steps-updated");
  }

  try {
    const res = await api.api.history.$delete(
      { query: { id } },
      { headers: getAuthHeaders() }
    );
    return res.ok;
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] DELETE failed, action queued locally.", err);
    return false;
  }
}

export async function clearPracticeHistoryFromD1(): Promise<boolean> {
  runWithBypass(() => {
    localStorage.removeItem("vedic_history");
  });
  safeDispatchVedicEvent("vedic-history-updated");
  safeDispatchVedicEvent("vedic-steps-updated");

  try {
    const res = await api.api.history.$delete(
      {},
      { headers: getAuthHeaders() }
    );
    return res.ok;
  } catch (err) {
    console.warn("[Cloudflare D1 SQL] TRUNCATE failed, cache cleared.", err);
    return false;
  }
}

